import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io as socketIO } from 'socket.io-client';
import axios from 'axios';
import ApiLink from 'API';

const ChatContext = createContext(null);

export const useChat = () => useContext(ChatContext);

const authHeaders = () => ({ withCredentials: true, headers: { token: localStorage.getItem('token') } });

export function ChatProvider({ children }) {
    const token = localStorage.getItem('token');
    const myId = localStorage.getItem('userId');

    const socketRef = useRef(null);
    const [connected, setConnected] = useState(false);
    const [rooms, setRooms] = useState([]);
    const [unreadByRoom, setUnreadByRoom] = useState({});
    const [onlineUsers, setOnlineUsers] = useState({}); // userId -> bool
    const [incomingCall, setIncomingCall] = useState(null); // { roomId, kind, from }
    const [activeCalls, setActiveCalls] = useState({}); // roomId -> { kind } — ongoing calls you can join anytime
    const [messagePing, setMessagePing] = useState(0); // bumps on each inbound message (drives FAB shake)
    const [activeRoomId, setActiveRoomId] = useState(null);
    const activeRoomRef = useRef(null);
    useEffect(() => { activeRoomRef.current = activeRoomId; }, [activeRoomId]);

    // listeners registry for per-component subscriptions (messages, webrtc, etc.)
    const listeners = useRef({});
    const emit = useCallback((event, ...args) => {
        if (socketRef.current) socketRef.current.emit(event, ...args);
    }, []);
    const on = useCallback((event, handler) => {
        if (!socketRef.current) return () => {};
        socketRef.current.on(event, handler);
        return () => socketRef.current && socketRef.current.off(event, handler);
    }, []);

    const totalUnread = Object.values(unreadByRoom).reduce((a, b) => a + b, 0);

    const loadRooms = useCallback(async () => {
        try {
            const res = await axios.get(`${ApiLink}/api/chat/rooms`, authHeaders());
            setRooms(res.data || []);
            const map = {};
            (res.data || []).forEach((r) => { if (r.unreadCount) map[r._id] = r.unreadCount; });
            setUnreadByRoom((prev) => ({ ...prev, ...map }));
        } catch (e) { /* ignore */ }
    }, []);

    // ── connect socket once ────────────────────────────────────────────────
    useEffect(() => {
        if (!token) return undefined;
        const socket = socketIO(ApiLink, {
            auth: { token },
            transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        socket.on('connect', () => setConnected(true));
        socket.on('disconnect', () => setConnected(false));

        socket.on('chat:unread_summary', ({ byRoom }) => setUnreadByRoom(byRoom || {}));

        socket.on('chat:user_status', ({ userId, online }) => {
            setOnlineUsers((prev) => ({ ...prev, [userId]: online }));
        });

        // full online set (on connect + on request) — keeps the member list accurate
        socket.on('chat:online_list', (ids) => {
            const map = {};
            (ids || []).forEach((id) => { map[id] = true; });
            setOnlineUsers(map);
        });

        socket.on('chat:new_message_alert', ({ roomId, preview, lastMessageType, lastMessageAt }) => {
            // bump unread unless the room is currently open
            if (activeRoomRef.current !== roomId) {
                setUnreadByRoom((prev) => ({ ...prev, [roomId]: (prev[roomId] || 0) + 1 }));
                setMessagePing((p) => p + 1); // trigger FAB shake
                try { navigator.vibrate && navigator.vibrate(200); } catch (e) { /* unsupported */ }
            }
            setRooms((prev) => {
                const idx = prev.findIndex((r) => r._id === roomId);
                if (idx === -1) { loadRooms(); return prev; }
                const updated = { ...prev[idx], lastMessage: preview, lastMessageType, lastMessageAt };
                const rest = prev.filter((_, i) => i !== idx);
                return [updated, ...rest];
            });
        });

        socket.on('chat:incoming_call', (payload) => {
            // ignore my own ring
            if (payload.from && payload.from._id === myId) return;
            setIncomingCall(payload);
        });

        // ongoing calls — lets a user join at any time (also after refresh)
        socket.on('chat:active_calls', (list) => {
            const map = {};
            (list || []).forEach((c) => { map[c.roomId] = { kind: c.kind }; });
            setActiveCalls(map);
        });
        socket.on('chat:call_active', ({ roomId, kind, active }) => {
            setActiveCalls((prev) => {
                const next = { ...prev };
                if (active) next[roomId] = { kind: kind || 'video' };
                else delete next[roomId];
                return next;
            });
            if (!active) {
                // a call ended → clear any stale incoming ring for that room
                setIncomingCall((prev) => (prev && prev.roomId === roomId ? null : prev));
            }
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (token) loadRooms();
    }, [token, loadRooms]);

    // Ring/vibrate continuously while a call is incoming — stop when cleared
    // (user answers / rejects) or after a 30s "missed" timeout.
    useEffect(() => {
        if (!incomingCall) return undefined;
        let stopped = false;
        const ring = () => { try { navigator.vibrate && navigator.vibrate([500, 300]); } catch (e) { /* unsupported */ } };
        ring();
        const interval = setInterval(() => { if (!stopped) ring(); }, 900);
        const missedTimer = setTimeout(() => setIncomingCall(null), 30000);
        return () => {
            stopped = true;
            clearInterval(interval);
            clearTimeout(missedTimer);
            try { navigator.vibrate && navigator.vibrate(0); } catch (e) { /* unsupported */ }
        };
    }, [incomingCall]);

    const markRoomRead = useCallback((roomId) => {
        setUnreadByRoom((prev) => {
            if (!prev[roomId]) return prev;
            const next = { ...prev };
            delete next[roomId];
            return next;
        });
    }, []);

    const clearIncomingCall = useCallback(() => setIncomingCall(null), []);

    const value = {
        socket: socketRef,
        connected,
        myId,
        rooms,
        setRooms,
        loadRooms,
        unreadByRoom,
        totalUnread,
        onlineUsers,
        incomingCall,
        clearIncomingCall,
        activeCalls,
        messagePing,
        activeRoomId,
        setActiveRoomId,
        markRoomRead,
        emit,
        on,
        authHeaders,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export default ChatContext;
